import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { Radar, Users, MonitorSmartphone, Globe2, ShieldAlert, Flag, History, TrendingUp, Fingerprint, MapPinned, Loader2, RefreshCw, Database, AlertTriangle, CheckCircle2, Gauge, Cpu, Server, ClipboardList, X, Clock, ToggleLeft, ToggleRight, Ban } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import AdminSystemStatus from "@/pages/admin/AdminSystemStatus";
import SitrepDrillMap from "@/components/admin/SitrepDrillMap";
import BlockIpDialog from "@/components/admin/BlockIpDialog";
import { toast } from "@/lib/toast";
import { formatError } from "@/lib/api";

const POLL_MS = 10000;

function StatTile({ icon: Icon, label, value, accent, testId, small }) {
  return (
    <div className="border border-sniper bg-surface p-4" data-testid={testId}>
      <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {label}
      </span>
      <div className={`font-heading mt-1.5 ${small ? "text-sm uppercase tracking-wide pt-2.5" : "text-3xl"} ${accent || "text-foreground"}`}>{small ? value : (value ?? 0)}</div>
    </div>
  );
}

function SitrepHistory() {
  const [days, setDays] = useState(30);
  const [granularity, setGranularity] = useState("daily");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/sitrep/history?days=${days}&granularity=${granularity}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [days, granularity]);

  const ret = data?.retention;
  return (
    <div className="border border-sniper bg-surface mt-6" data-testid="sitrep-history">
      <div className="px-4 py-3 border-b border-sniper flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
          <History className="w-4 h-4 text-olive-bright" /> Activity Trend
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Granularity — how the same data is bucketed */}
          <div className="inline-flex border border-sniper" data-testid="history-granularity">
            {[["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]].map(([g, label]) => (
              <button key={g} onClick={() => setGranularity(g)} data-testid={`history-granularity-${g}`}
                className={`font-mono text-[10px] uppercase px-2.5 py-1 transition-colors ${granularity === g ? "bg-olive text-white" : "text-muted-foreground hover:text-foreground"}`}>
                {label}
              </button>
            ))}
          </div>
          {/* Range — how far back */}
          <div className="flex items-center gap-1" data-testid="history-range">
            {[14, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`history-range-${d}`}
                className={`font-mono text-[10px] uppercase px-2.5 py-1 border transition-colors ${days === d ? "border-olive-bright text-foreground" : "border-sniper text-muted-foreground hover:text-foreground"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="p-4">
        {!data ? (
          <p className="font-mono text-xs uppercase text-muted-foreground">Loading…</p>
        ) : !(data.series || []).some((p) => p.active_users > 0 || p.new_signups > 0) ? (
          <div className="border border-dashed border-sniper py-10 flex flex-col items-center justify-center text-center" data-testid="history-empty">
            <TrendingUp className="w-6 h-6 text-muted-foreground/50 mb-2" strokeWidth={1.6} />
            <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">No recorded activity in the last {days} days</p>
            <p className="font-mono text-[10px] text-muted-foreground/60 mt-1">The trend fills in as members sign in and sign up — nothing to plot yet.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-6 mb-3 font-mono text-[11px] uppercase">
              <span className="text-secondary-foreground"><TrendingUp className="w-3.5 h-3.5 inline text-olive-bright" /> Peak DAU: <span className="text-foreground">{data.peak_active}</span></span>
              <span className="text-secondary-foreground">New signups ({days}d): <span className="text-foreground">{data.total_signups}</span></span>
            </div>
            <div style={{ width: "100%", height: 220 }} data-testid="history-chart">
              <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                <LineChart data={data.series} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke="#2A2E2A" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: "#8a8f82", fontSize: 9, fontFamily: "monospace" }} tickFormatter={(d) => d.slice(5)} minTickGap={20} />
                  <YAxis tick={{ fill: "#8a8f82", fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#14170f", border: "1px solid #2A2E2A", fontFamily: "monospace", fontSize: 11 }} labelStyle={{ color: "#fff" }} />
                  <Line type="monotone" dataKey="active_users" name="Active users" stroke="#CCFF00" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="new_signups" name="New signups" stroke="#6B7530" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
        {ret && (
          <div className="mt-4 border-t border-sniper pt-3 flex flex-wrap items-center gap-4 font-mono text-[10px] uppercase text-muted-foreground" data-testid="sitrep-retention-status">
            <span className="flex items-center gap-1.5"><Fingerprint className="w-3.5 h-3.5 text-olive-bright" /> IP retention: <span className="text-secondary-foreground">{ret.ip_retention_days}d → hashed</span></span>
            <span>Activity retention: <span className="text-secondary-foreground">{ret.activity_retention_days}d</span></span>
            <span>Pepper: {ret.ip_pepper_configured ? <span className="text-olive-bright">configured</span> : <span className="text-blaze">not set</span>}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function GeoReResolve() {
  const [dry, setDry] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);

  const runDry = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/sitrep/reresolve-geo");
      setDry(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/admin/sitrep/reresolve-geo?apply=true");
      setResult(data); setDry(null);
      toast.success(`Re-resolved ${data.resolved_sessions} session(s).`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  return (
    <div className="border border-sniper bg-surface mt-6 p-4" data-testid="sitrep-reresolve">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
            <MapPinned className="w-4 h-4 text-olive-bright" /> Re-resolve session geo
          </h2>
          <p className="text-[11px] font-mono text-muted-foreground mt-1 max-w-xl leading-relaxed">
            Historical sessions that only captured country-level data land in the map's "Unknown"
            bucket. This looks their IPs up again (rate-limited) and backfills region + city, then
            writes the run to Audit Events. New sessions already capture this on every ping, so it
            only fixes the backlog.
          </p>
        </div>
        <button onClick={runDry} disabled={busy || applying} data-testid="reresolve-dry-btn"
          className="shrink-0 inline-flex items-center gap-1.5 border border-olive/60 text-olive-bright hover:bg-olive/10 font-mono uppercase text-xs px-3 py-2 transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Check candidates
        </button>
      </div>

      {dry && (
        <div className="mt-3 border border-volt/30 bg-volt/5 p-3" data-testid="reresolve-dry-result">
          <p className="font-mono text-xs text-foreground">
            <span className="text-volt">{dry.candidate_sessions}</span> session(s) across{" "}
            <span className="text-volt">{dry.distinct_ips}</span> unique IP(s) can be re-resolved ·{" "}
            {dry.unknown_now} show Unknown · {dry.total_sessions} total.
          </p>
          <p className="font-mono text-[10px] text-muted-foreground mt-1">{dry.note}</p>
          {dry.distinct_ips > 0 ? (
            <button onClick={runApply} disabled={applying} data-testid="reresolve-apply-btn"
              className="mt-2.5 inline-flex items-center gap-1.5 border border-olive-bright/60 text-olive-bright hover:bg-olive/10 font-mono uppercase text-xs px-3 py-2 transition-colors disabled:opacity-50">
              {applying ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Resolving… (rate-limited)</> : <>Apply — look up {Math.min(dry.distinct_ips, 40)} IP(s)</>}
            </button>
          ) : (
            <p className="font-mono text-[10px] text-muted-foreground mt-2">Nothing to re-resolve.</p>
          )}
        </div>
      )}

      {result && (
        <div className="mt-3 border border-olive-bright/40 bg-olive/5 p-3 font-mono text-xs text-foreground" data-testid="reresolve-apply-result">
          Resolved <span className="text-olive-bright">{result.resolved_sessions}</span> session(s) across{" "}
          {result.ips_processed} IP(s). {result.still_unknown_sessions} stayed unknown.{" "}
          <span className="text-muted-foreground">{result.note}</span>
        </div>
      )}
    </div>
  );
}


function GeoLiteStatus() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/sitrep/geolite-status"); setSt(data); return data; }
    catch { return null; }
  };
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  const runUpdate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/sitrep/geolite-update");
      toast.success("GeoLite2 update started…");
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const d = await load();
        if (d && !d.updating) {
          clearInterval(pollRef.current); setBusy(false);
          if (d.last_error) toast.error("GeoLite2 update failed — see banner."); else toast.success("GeoLite2 database updated.");
        }
      }, 3000);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setBusy(false); }
  };

  if (!st) return null;
  const failed = !!st.last_error;
  const updating = st.updating || busy;
  const buildDate = st.build_date ? new Date(st.build_date).toLocaleDateString() : null;

  return (
    <div className="border border-sniper bg-surface mt-6 p-4" data-testid="sitrep-geolite">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
            <Database className="w-4 h-4 text-olive-bright" /> GeoLite2 Database
          </h2>
          <div className="flex items-center flex-wrap gap-2 mt-2">
            {st.active
              ? <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-olive-bright border border-olive/50 px-2 py-0.5" data-testid="geolite-active"><CheckCircle2 className="w-3 h-3" /> Active</span>
              : <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-muted-foreground border border-sniper px-2 py-0.5" data-testid="geolite-inactive">Not loaded · fallback geo</span>}
            {buildDate && <span className="font-mono text-[10px] uppercase text-secondary-foreground" data-testid="geolite-build">DB build {buildDate}{st.build_age_days != null ? ` · ${Math.round(st.build_age_days)}d old` : ""}</span>}
            {st.active && st.refresh_due && <span className="font-mono text-[10px] uppercase text-amber-400">refresh due</span>}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          {st.configured
            ? <button onClick={runUpdate} disabled={updating} data-testid="geolite-update-btn"
                className="inline-flex items-center gap-1.5 border border-olive/60 text-olive-bright hover:bg-olive/10 font-mono uppercase text-xs px-3 py-2 transition-colors disabled:opacity-50">
                {updating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Updating…</> : <><RefreshCw className="w-3.5 h-3.5" /> Update now</>}
              </button>
            : <span className="font-mono text-[10px] uppercase text-amber-400 border border-amber-400/40 px-2 py-1" data-testid="geolite-not-configured">MaxMind key not set</span>}
          <span className="font-mono text-[9px] uppercase text-muted-foreground/70">{st.downloads_today}/{st.max_downloads_per_day} downloads today</span>
        </div>
      </div>

      {failed && (
        <div className="mt-3 border border-blaze/50 bg-blaze/10 text-blaze font-mono text-[11px] px-3 py-2 flex items-start gap-2" data-testid="geolite-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Last update failed: {st.last_error}{st.last_success_at ? ` · last good ${new Date(st.last_success_at).toLocaleString()}` : ""}</span>
        </div>
      )}
      {!st.configured && (
        <p className="mt-3 font-mono text-[10px] text-muted-foreground leading-relaxed max-w-2xl">
          Add <span className="text-secondary-foreground">MAXMIND_LICENSE_KEY</span> (account ID {st.account_id_configured ? "set" : "unset"}) to enable self-hosted city/ASN geolocation. Until then the map uses Cloudflare / ip-api fallback (country/region/city, no accuracy radius).
        </p>
      )}
      <p className="mt-3 font-mono text-[9px] text-muted-foreground/60" data-testid="geolite-attribution">{st.attribution}</p>
    </div>
  );
}


function RecordMeasurementForm({ envDefault, onSaved, onCancel }) {
  const [form, setForm] = useState({
    environment: envDefault === "production" ? "production" : "preview",
    concurrent_held: "", errors_at_peak: "", feed_rps_ceiling: "", feed_p95_ms_at_200: "", note: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const numOk = (v) => v !== "" && Number.isFinite(Number(v)) && Number(v) >= 0;
  const valid = numOk(form.concurrent_held) && numOk(form.errors_at_peak) && numOk(form.feed_rps_ceiling) && numOk(form.feed_p95_ms_at_200);

  const submit = async () => {
    if (!valid) { toast.error("Enter the four numbers from your load test."); return; }
    setSaving(true);
    try {
      await api.post("/admin/status/capacity/measurement", {
        environment: form.environment,
        concurrent_held: Number(form.concurrent_held),
        errors_at_peak: Number(form.errors_at_peak),
        feed_rps_ceiling: Number(form.feed_rps_ceiling),
        feed_p95_ms_at_200: Number(form.feed_p95_ms_at_200),
        note: form.note || undefined,
      });
      toast.success("Measurement recorded — capacity state updated.");
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't save that measurement."); }
    finally { setSaving(false); }
  };

  const Field = ({ label, k, placeholder }) => (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <input type="number" min="0" value={form[k]} onChange={set(k)} placeholder={placeholder}
        data-testid={`capacity-record-${k.replace(/_/g, "-")}`}
        className="mt-1 w-full bg-obsidian border border-sniper focus:border-olive-bright outline-none px-2.5 py-2 font-mono text-sm text-foreground" />
    </label>
  );

  return (
    <div className="border border-olive/40 bg-olive/5 p-4 space-y-3" data-testid="capacity-record-form">
      <p className="font-mono text-[10px] uppercase tracking-widest text-olive-bright flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Record a measurement</p>
      <div className="border border-sniper bg-obsidian/60 p-3 font-body text-[11px] text-muted-foreground leading-relaxed" data-testid="capacity-record-help">
        Run the load test out-of-band (it can't run from this page), then enter the numbers it produced.
        Quick recipe: from a machine near the target, run <span className="font-mono text-secondary-foreground">python backend/scripts/loadtest.py</span> against
        the target URL, ramp concurrency until p95 latency or errors climb, and read off the peak it held.
        Select the environment you tested — that's what drives the mismatch warning, not an assumption.
      </div>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Environment tested</span>
        <select value={form.environment} onChange={set("environment")} data-testid="capacity-record-environment"
          className="mt-1 w-full bg-obsidian border border-sniper focus:border-olive-bright outline-none px-2.5 py-2 font-mono text-sm text-foreground">
          <option value="preview">Preview</option>
          <option value="production">Production</option>
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Concurrent held</span>
          <input type="number" min="0" value={form.concurrent_held} onChange={set("concurrent_held")} placeholder="e.g. 200"
            data-testid="capacity-record-concurrent-held"
            className="mt-1 w-full bg-obsidian border border-sniper focus:border-olive-bright outline-none px-2.5 py-2 font-mono text-sm text-foreground" />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Errors at peak</span>
          <input type="number" min="0" value={form.errors_at_peak} onChange={set("errors_at_peak")} placeholder="e.g. 0"
            data-testid="capacity-record-errors-at-peak"
            className="mt-1 w-full bg-obsidian border border-sniper focus:border-olive-bright outline-none px-2.5 py-2 font-mono text-sm text-foreground" />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Feed req/s ceiling</span>
          <input type="number" min="0" value={form.feed_rps_ceiling} onChange={set("feed_rps_ceiling")} placeholder="e.g. 230"
            data-testid="capacity-record-feed-rps-ceiling"
            className="mt-1 w-full bg-obsidian border border-sniper focus:border-olive-bright outline-none px-2.5 py-2 font-mono text-sm text-foreground" />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Feed p95 @200 (ms)</span>
          <input type="number" min="0" value={form.feed_p95_ms_at_200} onChange={set("feed_p95_ms_at_200")} placeholder="e.g. 945"
            data-testid="capacity-record-feed-p95-ms-at-200"
            className="mt-1 w-full bg-obsidian border border-sniper focus:border-olive-bright outline-none px-2.5 py-2 font-mono text-sm text-foreground" />
        </label>
      </div>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Note (optional)</span>
        <input type="text" maxLength={500} value={form.note} onChange={set("note")} data-testid="capacity-record-note"
          className="mt-1 w-full bg-obsidian border border-sniper focus:border-olive-bright outline-none px-2.5 py-2 font-mono text-sm text-foreground" />
      </label>
      <div className="flex items-center gap-2">
        <button onClick={submit} disabled={saving || !valid} data-testid="capacity-record-submit"
          className="inline-flex items-center gap-1.5 border border-olive-bright/60 text-olive-bright hover:bg-olive/10 font-mono uppercase text-xs px-3 py-2 transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Save measurement
        </button>
        <button onClick={onCancel} disabled={saving} data-testid="capacity-record-cancel"
          className="inline-flex items-center gap-1.5 border border-sniper text-muted-foreground hover:text-foreground font-mono uppercase text-xs px-3 py-2 transition-colors">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </div>
  );
}

const STATE_STYLE = {
  current: { text: "text-olive-bright", border: "border-olive/50", label: "CURRENT", Icon: CheckCircle2 },
  stale: { text: "text-amber-400", border: "border-amber-400/50", label: "STALE", Icon: Clock },
  not_measured: { text: "text-muted-foreground", border: "border-sniper", label: "NOT MEASURED", Icon: AlertTriangle },
};

// Launch-affecting MASTER SWITCHES surfaced so a thrown switch is never forgotten. A switch that
// lives only in config is a switch someone forgets is thrown.
function FeatureSwitches() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/admin/system/feature-switches").then(({ data }) => { setData(data); setErr(false); }).catch(() => setErr(true));
  }, []);
  const switches = data?.switches || [];
  return (
    <div className="border border-sniper bg-surface mt-6" data-testid="sitrep-feature-switches">
      <div className="px-4 py-3 border-b border-sniper flex items-center gap-2">
        <ToggleLeft className="w-4 h-4 text-olive-bright" />
        <h2 className="font-heading text-lg uppercase tracking-tight text-foreground">Feature switches</h2>
        <span className="font-mono text-[10px] uppercase text-muted-foreground ml-1">launch-affecting master flags</span>
      </div>
      {err && !data && <p className="p-4 font-mono text-xs text-blaze" data-testid="feature-switches-error">Could not load feature switches.</p>}
      {switches.length > 0 && (
        <div className="divide-y divide-sniper">
          {switches.map((s) => {
            const on = !!s.enabled;
            return (
              <div key={s.key} className="px-4 py-3.5 flex items-start gap-3" data-testid={`feature-switch-${s.key}`}>
                {on
                  ? <ToggleRight className="w-6 h-6 text-olive-bright shrink-0" />
                  : <ToggleLeft className="w-6 h-6 text-muted-foreground shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-body text-sm text-foreground font-semibold uppercase tracking-tight">{s.label}</span>
                    <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${on ? "text-olive-bright border-olive/50" : "text-amber-400 border-amber-400/50"}`} data-testid={`feature-switch-state-${s.key}`}>
                      {on ? "ON" : "OFF"}
                    </span>
                    {s.key === "platform_labels" && !on && (
                      <span className="font-mono text-[10px] uppercase px-2 py-0.5 border text-amber-400 border-amber-400/50" data-testid="feature-switch-easypost-note">EasyPost pending</span>
                    )}
                  </div>
                  <p className="font-mono text-[11px] text-muted-foreground mt-1">{s.note}</p>
                  <p className="font-mono text-[10px] text-muted-foreground/60 mt-0.5">env: {s.env_var}{s.easypost ? ` · key ${s.easypost.key_present ? s.easypost.mode : "absent"}${s.easypost.armed ? " · armed" : ""}${s.easypost.blocked_by_guard ? " · guard-blocked" : ""}` : ""}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CapacityCard() {
  const [cap, setCap] = useState(null);
  const [err, setErr] = useState(false);
  const [recording, setRecording] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () => api.get("/admin/status/capacity").then(({ data }) => { setCap(data); setErr(false); }).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/status/capacity/measurement/run");
      const mm = data.measurement || {};
      toast.success(`Load test done on ${mm.environment} — held ${mm.concurrent_held} concurrent, feed ~${mm.feed_rps_ceiling} req/s, ${mm.errors_at_peak} errors at peak.`);
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Load test could not run."); }
    finally { setRunning(false); }
  };

  const rt = cap?.runtime || {};
  const det = rt.detected || {};
  const prod = cap?.production_capacity || {};
  const m = cap?.measured;
  const state = cap?.measurement_state || {};
  const sty = STATE_STYLE[state.state] || STATE_STYLE.not_measured;
  const envLabel = (cap?.environment || "unknown").toUpperCase();
  const envColor = cap?.environment === "production" ? "text-olive-bright border-olive/50"
    : cap?.environment === "preview" ? "text-volt border-volt/40" : "text-muted-foreground border-sniper";
  const UNK = <span className="text-amber-400">UNKNOWN</span>;

  // A runtime field renders ONLY when it was genuinely detected — otherwise UNKNOWN, never a guess.
  const rtField = (key, value) => (det[key] ? value : UNK);

  return (
    <div className="border border-sniper bg-surface mt-6" data-testid="sitrep-capacity">
      <div className="px-4 py-3 border-b border-sniper flex items-center gap-2 flex-wrap">
        <Gauge className="w-4 h-4 text-olive-bright" />
        <h2 className="font-heading text-lg uppercase tracking-tight text-foreground">Capacity</h2>
        <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${envColor}`} data-testid="capacity-env">{envLabel}</span>
        <span className={`ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] uppercase px-2 py-0.5 border ${sty.border} ${sty.text}`} data-testid="capacity-state">
          <sty.Icon className="w-3 h-3" /> {sty.label}
        </span>
      </div>

      {err && !cap && <p className="p-4 font-mono text-xs text-blaze" data-testid="capacity-error">Could not load capacity.</p>}

      {cap && (
        <div className="p-4 space-y-4">
          {/* env-mismatch warning — driven by the recorded environment field */}
          {cap.env_mismatch && (
            <div className="border border-amber-400/50 bg-amber-400/10 p-3 flex items-start gap-2" data-testid="capacity-env-mismatch">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="font-mono text-[11px] text-amber-300 leading-relaxed">
                This measurement was taken on <span className="uppercase text-amber-200">{m?.environment}</span> but you're
                viewing it on <span className="uppercase text-amber-200">{cap.environment}</span>. The numbers below do NOT
                describe this environment. Record a measurement on {cap.environment} to see its real ceiling.
              </p>
            </div>
          )}

          {/* HEADLINE — concurrent requests held */}
          <div className="border border-sniper bg-obsidian/40 p-4" data-testid="capacity-headline">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Concurrent requests held</p>
            <div className="flex items-end gap-3 mt-1 flex-wrap">
              <span className="font-heading text-olive-bright leading-none" style={{ fontWeight: 800, fontSize: "64px" }} data-testid="capacity-headline-number">
                {m && m.concurrent_held != null ? m.concurrent_held : "—"}
              </span>
              {m && (
                <span className="font-mono text-[11px] text-secondary-foreground pb-2">
                  · {m.errors_at_peak ?? 0} errors at peak · measured on <span className={m.environment === "production" ? "text-olive-bright" : "text-volt"}>{m.environment}</span>
                </span>
              )}
            </div>
            {state.recorded_at && (
              <p className="font-mono text-[10px] text-muted-foreground mt-1">
                Recorded {new Date(state.recorded_at).toLocaleDateString()}{state.age_days != null ? ` · ${state.age_days}d ago` : ""}{state.recorded_by ? ` · by ${state.recorded_by}` : ""}
              </p>
            )}
            {(state.reasons || []).length > 0 && (
              <ul className="mt-2 space-y-0.5" data-testid="capacity-state-reasons">
                {state.reasons.map((r, i) => (
                  <li key={i} className={`font-mono text-[10px] flex items-start gap-1.5 ${sty.text}`}><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /> {r}</li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button onClick={runNow} disabled={running} data-testid="capacity-run-now"
                className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-obsidian font-heading font-bold uppercase text-sm px-4 py-2 transition-colors">
                {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Running load test…</> : <><Gauge className="w-4 h-4" /> Run load test now</>}
              </button>
              {!recording ? (
                <button onClick={() => setRecording(true)} data-testid="capacity-record-open"
                  className="inline-flex items-center gap-1.5 border border-olive/60 text-olive-bright hover:bg-olive/10 font-mono uppercase text-xs px-3 py-2 transition-colors">
                  <ClipboardList className="w-3.5 h-3.5" /> Enter numbers manually
                </button>
              ) : null}
              {running && (
                <span className="font-mono text-[10px] text-muted-foreground" data-testid="capacity-run-note">
                  Briefly loads this pod (~30s). On production this measures the real ceiling.
                </span>
              )}
            </div>
            {recording && (
              <div className="mt-3">
                <RecordMeasurementForm envDefault={cap.environment} onCancel={() => setRecording(false)}
                  onSaved={() => { setRecording(false); load(); }} />
              </div>
            )}
          </div>

          {/* THIS POD runtime (detected per-field, UNKNOWN when not read) */}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Server className="w-3 h-3" /> This pod — detected runtime</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-xs">
              <div className="border border-sniper p-2.5" data-testid="capacity-workers"><span className="text-muted-foreground block text-[10px] uppercase">Workers</span><span className="text-foreground text-base">{rtField("workers", rt.workers)}</span></div>
              <div className="border border-sniper p-2.5" data-testid="capacity-reload"><span className="text-muted-foreground block text-[10px] uppercase">Autoreload</span><span className={rt.reload ? "text-amber-400 text-base" : "text-foreground text-base"}>{rtField("reload", rt.reload ? "ON" : "off")}</span></div>
              <div className="border border-sniper p-2.5" data-testid="capacity-vcpu"><span className="text-muted-foreground block text-[10px] uppercase">vCPU</span><span className="text-foreground text-base">{rtField("vcpu", rt.vcpu)}</span></div>
              <div className="border border-sniper p-2.5" data-testid="capacity-mem"><span className="text-muted-foreground block text-[10px] uppercase">Memory</span><span className="text-foreground text-base">{rtField("mem", rt.mem_mb != null ? `${Math.round(rt.mem_mb / 1024)} GB` : null)}</span></div>
            </div>
          </div>

          {/* warnings */}
          {(cap.warnings || []).length > 0 && (
            <div className="border border-amber-400/40 bg-amber-400/5 p-3 space-y-1" data-testid="capacity-warnings">
              {cap.warnings.map((w, i) => (
                <p key={i} className="font-mono text-[11px] text-amber-300 flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {w}</p>
              ))}
            </div>
          )}

          {/* PRODUCTION capacity — known hardware, UNKNOWN ceiling, provably BELOW preview */}
          <div className="border border-sniper p-3" data-testid="capacity-production">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Production capacity</p>
            {prod.spec && (
              <p className="font-mono text-[11px] text-foreground mb-2" data-testid="capacity-prod-spec">
                <span className="text-volt">{prod.spec.plan}</span> · {prod.spec.vcpu} vCPU · {prod.spec.mem_gb} GB
                <span className="text-amber-400"> — {prod.spec.vs_preview}</span>
              </p>
            )}
            {prod.status === "unknown" ? (
              <>
                <span className="inline-flex items-center gap-1.5 font-mono text-xs uppercase text-amber-400 border border-amber-400/40 px-2 py-0.5" data-testid="capacity-prod-unknown"><AlertTriangle className="w-3 h-3" /> Ceiling unknown — below preview</span>
                <p className="mt-2 font-body text-xs text-secondary-foreground">{prod.reason}</p>
              </>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-mono text-xs uppercase text-olive-bright border border-olive/50 px-2 py-0.5" data-testid="capacity-prod-known"><CheckCircle2 className="w-3 h-3" /> {prod.workers} workers · reload {prod.reload ? "on" : "off"}</span>
            )}
          </div>

          {/* MEASURED ceiling detail */}
          {m ? (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                <Cpu className="w-3 h-3" /> Measured ceiling
                <span className="text-volt border border-volt/40 px-1.5 py-0.5" data-testid="capacity-measured-label">measured on {m.environment || "preview"}</span>
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 font-mono text-xs mb-3">
                <div className="border border-sniper p-2.5" data-testid="capacity-concurrent"><span className="text-muted-foreground block text-[10px] uppercase">Held concurrent</span><span className="text-olive-bright text-base">{m.concurrent_held} · {m.errors_at_peak} errors</span></div>
                <div className="border border-sniper p-2.5" data-testid="capacity-feed-rps"><span className="text-muted-foreground block text-[10px] uppercase">Feed ceiling</span><span className="text-foreground text-base">~{m.feed_rps_ceiling} req/s</span></div>
                <div className="border border-sniper p-2.5" data-testid="capacity-feed-p95"><span className="text-muted-foreground block text-[10px] uppercase">Feed p95 @200</span><span className="text-foreground text-base">{m.feed_p95_ms_at_200} ms</span></div>
              </div>
              {m.bottleneck && <p className="font-body text-[11px] text-muted-foreground mb-2">Bottleneck: {m.bottleneck}</p>}
              {(m.endpoints || []).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full font-mono text-[11px]" data-testid="capacity-endpoints">
                    <thead><tr className="text-left text-muted-foreground uppercase text-[9px] border-b border-sniper"><th className="px-2 py-1.5">Endpoint</th><th className="px-2 py-1.5 text-right">req/s</th><th className="px-2 py-1.5 text-right">p95 @200</th></tr></thead>
                    <tbody className="divide-y divide-sniper">
                      {m.endpoints.map((e) => (
                        <tr key={e.name} className="text-secondary-foreground">
                          <td className="px-2 py-1.5 text-foreground">{e.name}</td>
                          <td className="px-2 py-1.5 text-right">~{e.rps}</td>
                          <td className="px-2 py-1.5 text-right">{e.p95_ms_at_200} ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <p className="font-mono text-[11px] text-muted-foreground" data-testid="capacity-no-measurement">No load test recorded yet — record one to establish a ceiling.</p>
          )}

          {/* ranked next steps: multiplier ÷ hours */}
          {(cap.next_steps || []).length > 0 && (
            <div data-testid="capacity-next-steps">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Highest-ROI next steps <span className="text-muted-foreground/60 normal-case">(capacity multiplier ÷ hours)</span></p>
              <div className="space-y-2">
                {cap.next_steps.map((s) => (
                  <div key={s.rank} className="border border-sniper p-3 flex items-start gap-3" data-testid={`capacity-step-${s.rank}`}>
                    <span className="font-heading text-olive-bright text-xl leading-none pt-0.5">{s.rank}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-body text-xs text-foreground">{s.step}</p>
                      <p className="font-body text-[11px] text-muted-foreground mt-0.5">{s.detail}</p>
                    </div>
                    <div className="shrink-0 text-right font-mono text-[10px]">
                      <div className="text-olive-bright text-sm">{s.roi}×/hr</div>
                      <div className="text-muted-foreground">{s.multiplier}× · {s.hours}h</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function AdminSITREP() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockedIps, setBlockedIps] = useState(new Set());
  const timer = useRef(null);

  const loadBlockedIps = () => {
    api.get("/admin/ip-blocks?status=active")
      .then(({ data }) => setBlockedIps(new Set(data.active_ips || [])))
      .catch(() => {});
  };

  const load = () => {
    if (document.hidden) return;
    api.get("/admin/sitrep/overview")
      .then(({ data }) => { setData(data); setError(false); })
      .catch(() => setError(true));
  };

  useEffect(() => {
    load();
    loadBlockedIps();
    timer.current = setInterval(load, POLL_MS);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(timer.current); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const stats = data?.stats || {};
  const points = data?.map_points || [];
  const alerts = data?.concurrent_alerts || [];
  const activity = data?.live_activity || [];

  return (
    <div className="text-foreground" data-testid="admin-sitrep">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Radar className="w-6 h-6 text-olive-bright" strokeWidth={1.7} />
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">SITREP</h1>
        </div>
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-volt opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-volt" />
            </span>
            Live · last {data?.window_minutes || 10} min
          </span>
        </div>
      </div>
      <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">
        Real-time active sessions · geolocation · first-party tracking
        {stats.vpn_detection_enabled === false && <span className="text-muted-foreground/70"> · VPN detection off</span>}
      </p>

      {error && !data && (
        <div className="border border-blaze/50 bg-blaze/10 text-blaze font-mono text-sm p-4 mt-5" data-testid="sitrep-error">
          Could not load SITREP feed. Retrying…
        </div>
      )}

      {/* stat row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-5" data-testid="sitrep-stats">
        <StatTile icon={Users} label="Active Now" value={stats.active_users} accent="text-olive-bright" testId="sitrep-stat-active" />
        <StatTile icon={MonitorSmartphone} label="Sessions" value={stats.active_sessions} testId="sitrep-stat-sessions" />
        <StatTile icon={Flag} label="US" value={stats.us} accent="text-olive-bright" testId="sitrep-stat-us" />
        <StatTile icon={Globe2} label="Non-US" value={stats.non_us} accent="text-volt" testId="sitrep-stat-nonus" />
        {stats.vpn_detection_enabled === false
          ? <StatTile icon={ShieldAlert} label="VPN" value="Detection off" accent="text-muted-foreground" testId="sitrep-stat-vpn" small />
          : <StatTile icon={ShieldAlert} label="VPN Flagged" value={stats.vpn} accent="text-blaze" testId="sitrep-stat-vpn" />}
        <StatTile icon={ShieldAlert} label="Alerts" value={stats.alerts} accent="text-blaze" testId="sitrep-stat-alerts" />
      </div>

      <SitrepHistory />

      <GeoLiteStatus />

      <GeoReResolve />

      {/* concurrent-session alerts */}
      {alerts.length > 0 && (
        <div className="border border-blaze/50 bg-blaze/10 mt-6" data-testid="sitrep-alerts">
          <div className="px-4 py-3 border-b border-blaze/40 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-blaze" />
            <h2 className="font-heading text-lg uppercase tracking-tight text-blaze">Concurrent Session Alerts</h2>
            <span className="font-mono text-[10px] uppercase text-muted-foreground">possible account sharing</span>
          </div>
          <div className="divide-y divide-blaze/20">
            {alerts.map((a) => (
              <div key={a.user_id} className="px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid={`sitrep-alert-${a.user_id}`}>
                <div className="min-w-0">
                  <span className="font-mono text-sm text-foreground">@{a.username}</span>
                  <span className="font-mono text-xs text-muted-foreground"> · {a.email}</span>
                  {a.any_vpn && <span className="ml-2 font-mono text-[10px] uppercase border border-blaze/60 text-blaze px-1.5 py-0.5">VPN</span>}
                </div>
                <div className="font-mono text-[11px] text-secondary-foreground">
                  <span className="text-blaze">{a.distinct_ips} IPs</span> · {a.session_count} sessions
                  {a.countries?.length ? <span className="text-muted-foreground"> · {a.countries.join(", ")}</span> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* geo drill-down map: World -> USA -> State -> City -> IP location -> IP details */}
      <SitrepDrillMap />

      {/* live activity table */}
      <div className="border border-sniper bg-surface mt-6" data-testid="sitrep-activity">
        <div className="px-4 py-3 border-b border-sniper">
          <h2 className="font-heading text-lg uppercase tracking-tight text-foreground">Live Activity</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full font-mono text-xs">
            <thead>
              <tr className="text-left text-muted-foreground uppercase text-[10px] border-b border-sniper">
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">IP</th>
                <th className="px-3 py-2">Flags</th>
                <th className="px-3 py-2">Last seen</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sniper">
              {activity.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-5 text-muted-foreground">No active sessions in the last {data?.window_minutes || 10} minutes.</td></tr>
              )}
              {activity.map((r, i) => {
                const ipBlocked = r.ip && blockedIps.has(r.ip);
                return (
                <tr key={i} className="text-secondary-foreground" data-testid="sitrep-activity-row">
                  <td className="px-3 py-2 text-foreground">@{r.username}{r.role === "admin" && <span className="ml-1.5 text-[9px] uppercase text-blaze border border-blaze/50 px-1">admin</span>}</td>
                  <td className="px-3 py-2">
                    <span className={r.is_us ? "text-olive-bright" : "text-volt"}>{r.country_name || r.country_code || "Unknown"}</span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.ip || "—"}</td>
                  <td className="px-3 py-2">
                    {stats.vpn_detection_enabled === false
                      ? <span className="text-[9px] uppercase text-muted-foreground/60">off</span>
                      : r.is_vpn
                        ? <span className="text-[9px] uppercase text-blaze border border-blaze/50 px-1">VPN</span>
                        : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{timeAgo(r.last_seen)}</td>
                  <td className="px-3 py-2 text-right">
                    {ipBlocked ? (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase text-blaze border border-blaze/50 px-1.5 py-0.5" data-testid={`sitrep-ip-blocked-${i}`}>
                        <Ban className="w-3 h-3" /> Blocked
                      </span>
                    ) : (
                      <button onClick={() => setBlockTarget({ ip: r.ip, userId: r.user_id, username: r.username })}
                        data-testid={`sitrep-block-row-${i}`}
                        className="inline-flex items-center gap-1 border border-blaze/50 text-blaze hover:bg-blaze/10 font-mono uppercase text-[10px] px-2 py-1 transition-colors">
                        <Ban className="w-3 h-3" /> Block
                      </button>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {/* System Status folded into SITREP */}
      <FeatureSwitches />

      <CapacityCard />

      <AdminSystemStatus />

      {blockTarget && (
        <BlockIpDialog target={blockTarget}
          onClose={() => setBlockTarget(null)}
          onDone={() => { setBlockTarget(null); loadBlockedIps(); }} />
      )}
    </div>
  );
}
